import { Component, OnInit } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import { AuthService } from 'src/app/auth/auth.service';
import { LocalStorageService } from 'src/app/services/local-storage.service';
import { LocalStorageKeys } from 'src/app/core/enums/LocalStorageKeys';
import { normalizeLanguageCode } from 'src/app/core/utils/languages.util';

/**
 * Página pública (sin MsalGuard) a la que se redirige a los conductores.
 * Al ser pública no hay nada que redispare el login, evitando el bucle de redirects.
 */
@Component({
  selector: 'app-access-denied',
  template: `
    <div class="access-denied">
      <div class="access-denied__card">
        <div class="access-denied__icon" aria-hidden="true">
          <svg width="32" height="32" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path d="M12 8v5m0 3h.01M10.29 3.86 1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0Z"
              stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
          </svg>
        </div>
        <h1 class="access-denied__title">{{ 'DRIVER_BLOCKED.TITLE' | translate }}</h1>
        <p class="access-denied__message">{{ 'DRIVER_BLOCKED.MESSAGE' | translate }}</p>
        <button class="access-denied__button" type="button" (click)="logout()">
          {{ 'DRIVER_BLOCKED.LOGOUT' | translate }}
        </button>
      </div>
    </div>
  `,
  styles: [`
    .access-denied {
      display: flex;
      align-items: center;
      justify-content: center;
      min-height: 100vh;
      padding: 24px;
      background: var(--Green-cafler-50, #EEFEF5);
    }
    .access-denied__card {
      max-width: 440px;
      width: 100%;
      text-align: center;
      background: #fff;
      border-radius: 16px;
      padding: 40px 32px;
      border-top: 6px solid var(--Green-cafler-800, #36A565);
      box-shadow: 0 10px 30px rgba(54, 165, 101, 0.12);
    }
    .access-denied__icon {
      display: inline-flex;
      align-items: center;
      justify-content: center;
      width: 64px;
      height: 64px;
      margin-bottom: 16px;
      border-radius: 50%;
      color: var(--Green-cafler-800, #36A565);
      background: var(--Green-cafler-100, #DEFEEB);
    }
    .access-denied__title {
      font-size: 22px;
      font-weight: 700;
      margin: 0 0 12px;
      color: #1a1a1a;
    }
    .access-denied__message {
      font-size: 15px;
      line-height: 1.5;
      color: #555;
      margin: 0 0 28px;
    }
    .access-denied__button {
      border: none;
      border-radius: 10px;
      padding: 12px 28px;
      font-size: 15px;
      font-weight: 600;
      color: #1a1a1a;
      background: var(--Green-cafler-700, #48CE80);
      cursor: pointer;
      transition: background 0.15s ease;
    }
    .access-denied__button:hover {
      background: var(--Green-cafler-800, #36A565);
      color: #fff;
    }
  `],
})
export class AccessDeniedComponent implements OnInit {
  constructor(
    private readonly authService: AuthService,
    private readonly translate: TranslateService,
    private readonly localStorage: LocalStorageService
  ) {}

  ngOnInit(): void {
    // Al ser una ruta pública, el idioma puede no estar inicializado todavía (el sistema de
    // idiomas espera al perfil de usuario). Fijamos uno ya para que no se vean las claves i18n.
    if (!this.translate.currentLang) {
      const saved = this.localStorage.get<string>(LocalStorageKeys.Language);
      const lang = normalizeLanguageCode(saved || navigator.language);
      this.translate.use(lang);
    }
  }

  logout(): void {
    this.authService.logout();
  }
}
